import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Table, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createRole, fetchRoles, updateRole } from '../../utils/users';

import { useIsMobile } from '../../hooks/useIsMobile';

const RolesTab = ({ isActive }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const roleLabel = useCallback(
    (record) => {
      if (i18n.language === 'en') {
        return record.role_name_en || record.role_name_zh;
      }
      return record.role_name_zh || record.role_name_en;
    },
    [i18n.language]
  );

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await fetchRoles();
      setRoles(res.roles || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;
    loadData();
  }, [isActive, loadData]);

  const openCreateModal = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = useCallback(
    (record) => {
      setEditing(record);
      form.setFieldsValue({
        roleId: record.role_id,
        roleNameZh: record.role_name_zh,
        roleNameEn: record.role_name_en,
        roleNameVi: record.role_name_vi,
        roleNameTh: record.role_name_th
      });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editing) {
        await updateRole(editing.role_id, {
          roleNameZh: values.roleNameZh,
          roleNameEn: values.roleNameEn,
          roleNameVi: values.roleNameVi,
          roleNameTh: values.roleNameTh
        });
        message.success(t('roles.updateSuccess'));
      } else {
        await createRole(values);
        message.success(t('roles.createSuccess'));
      }

      closeModal();
      await loadData();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || t('roles.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      { title: t('roles.roleId'), dataIndex: 'role_id', key: 'role_id' },
      {
        title: t('roles.name'),
        key: 'name',
        render: (_, record) => roleLabel(record)
      },
      {
        title: t('roles.actions'),
        key: 'actions',
        render: (_, record) => (
          <Button type="link" onClick={() => openEditModal(record)}>
            {t('roles.edit')}
          </Button>
        )
      }
    ],
    [openEditModal, roleLabel, t]
  );

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          {t('roles.add')}
        </Button>
      </div>

      <Table
        rowKey="role_id"
        loading={loading}
        columns={columns}
        dataSource={roles}
        pagination={{ pageSize: 10, size: isMobile ? 'small' : 'default' }}
        scroll={{ x: 'max-content' }}
        className="system-table"
      />

      <Modal
        title={editing ? t('roles.editTitle') : t('roles.addTitle')}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        destroyOnClose
        width={isMobile ? '100%' : 640}
        className="system-modal"
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="roleId"
            label={t('roles.roleId')}
            rules={[{ required: true, message: t('roles.roleIdRequired') }]}
          >
            <Input disabled={Boolean(editing)} placeholder={t('roles.roleIdPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="roleNameZh"
            label={t('roles.nameZh')}
            rules={[{ required: true, message: t('roles.nameZhRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="roleNameEn"
            label={t('roles.nameEn')}
            rules={[{ required: true, message: t('roles.nameEnRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="roleNameVi"
            label={t('roles.nameVi')}
            rules={[{ required: true, message: t('roles.nameViRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="roleNameTh"
            label={t('roles.nameTh')}
            rules={[{ required: true, message: t('roles.nameThRequired') }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RolesTab;
